
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const NPVCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState<string>('100000');
  const [discountRate, setDiscountRate] = useState<string>('10');
  const [cashFlows, setCashFlows] = useState<string>('20000\n25000\n30000\n35000\n40000');
  const [npv, setNPV] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateNPV = () => {
    try {
      setError(null);
      
      const initial = parseFloat(initialInvestment);
      const rate = parseFloat(discountRate) / 100;
      const flows = cashFlows.split('\n').map(flow => parseFloat(flow.trim())).filter(flow => !isNaN(flow));

      if (isNaN(initial)) {
        setError("Please enter a valid initial investment");
        return;
      }
      if (isNaN(rate)) {
        setError("Please enter a valid discount rate");
        return;
      }
      if (flows.length === 0) {
        setError("Please enter at least one cash flow");
        return;
      }

      let presentValue = 0;
      flows.forEach((flow, index) => {
        presentValue += flow / Math.pow(1 + rate, index + 1);
      });

      const netPresentValue = presentValue - initial;
      setNPV(netPresentValue);

    } catch (err) {
      setError("Error calculating NPV. Please check your inputs.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">NPV (Net Present Value) Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="initial">Initial Investment ($)</Label>
              <Input
                id="initial"
                type="number"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="rate">Discount Rate (%)</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                value={discountRate}
                onChange={(e) => setDiscountRate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="flows">Cash Flows (one per line)</Label>
              <Textarea
                id="flows"
                value={cashFlows}
                onChange={(e) => setCashFlows(e.target.value)}
                className="mt-1 h-32"
                placeholder="Enter cash flows, one per line"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter each year's expected cash flow on a separate line
              </p>
            </div>

            <Button onClick={calculateNPV} className="w-full">
              Calculate NPV
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {npv !== null && (
              <div className="mt-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium">Net Present Value</h3>
                  <p className={`text-2xl font-bold ${npv >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {npv >= 0 ? '+$' : '-$'}{Math.abs(npv).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {npv >= 0 ? 'This investment is profitable' : 'This investment is not profitable'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NPVCalculator;
